import { isJwtExpired } from '@/utils/jwt'
import { filenameFromContentDisposition } from '@/utils/file-download'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** Payload extra do corpo do erro além de `error.code`/`error.message` (ex: `errors` de validação por linha). */
  readonly details?: unknown
  /** ID da requisição na API (header `X-Request-Id`) */
  readonly requestId?: string

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
    requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.requestId = requestId
  }
}

function requestIdFrom(response: Response, err: { requestId?: unknown }): string | undefined {
  if (typeof err.requestId === 'string') return err.requestId
  return response.headers.get('x-request-id') ?? undefined
}

export type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  token?: string
}

export type BlobRequestOptions = Omit<RequestInit, 'body'> & {
  token?: string
}

// Em produção, apiUrl vem de window.__NAVEGA_CONFIG__ (injetado pelo server.mjs via /config.js).
// Em dev, apiUrl é vazio e o Vite proxy encaminha /api/* para o backend sem CORS.
function createApiClient(prefix: string) {
  const BASE_URL = (window.__NAVEGA_CONFIG__?.apiUrl ?? import.meta.env.VITE_API_URL ?? '') + prefix

  async function requestFull<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { body, token, headers, ...rest } = options

    const response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

    if (response.status === 204) return undefined as T

    const isJson = response.headers.get('content-type')?.includes('application/json')
    const json = isJson ? await response.json() : undefined

    if (!response.ok) {
      const err = json?.error ?? {}
      if (
        response.status === 401 &&
        (err.code === 'TOKEN_EXPIRED' || (token && isJwtExpired(token)))
      ) {
        window.dispatchEvent(new CustomEvent('navega:session-expired'))
      }
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
        undefined,
        requestIdFrom(response, err),
      )
    }

    return (json ?? undefined) as T
  }

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { body, token, headers, ...rest } = options

    const response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

    if (response.status === 204) return undefined as T

    const isJson = response.headers.get('content-type')?.includes('application/json')
    const json = isJson ? await response.json() : undefined

    if (!response.ok) {
      const err = json?.error ?? {}
      if (
        response.status === 401 &&
        (err.code === 'TOKEN_EXPIRED' || (token && isJwtExpired(token)))
      ) {
        window.dispatchEvent(new CustomEvent('navega:session-expired'))
      }
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
        json?.errors,
        requestIdFrom(response, err),
      )
    }

    return (json?.data ?? undefined) as T
  }

  async function requestBlob(path: string, options: BlobRequestOptions = {}): Promise<Blob> {
    const { token, headers, ...rest } = options
    const response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    })
    if (!response.ok) {
      const json = await response.json().catch(() => ({}))
      const err = json?.error ?? {}
      if (
        response.status === 401 &&
        (err.code === 'TOKEN_EXPIRED' || (token && isJwtExpired(token)))
      ) {
        window.dispatchEvent(new CustomEvent('navega:session-expired'))
      }
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
        undefined,
        requestIdFrom(response, err),
      )
    }
    return response.blob()
  }

  async function requestPostBlob(
    path: string,
    body: unknown,
    options: BlobRequestOptions = {},
  ): Promise<{ blob: Blob; filename: string }> {
    const { token, headers, ...rest } = options
    const response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const json = await response.json().catch(() => ({}))
      const err = json?.error ?? {}
      if (
        response.status === 401 &&
        (err.code === 'TOKEN_EXPIRED' || (token && isJwtExpired(token)))
      ) {
        window.dispatchEvent(new CustomEvent('navega:session-expired'))
      }
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
        undefined,
        requestIdFrom(response, err),
      )
    }
    const blob = await response.blob()
    return { blob, filename: filenameFromContentDisposition(response, 'download') }
  }

  async function requestMultipart<T>(
    path: string,
    body: FormData,
    options: BlobRequestOptions = {},
  ): Promise<T> {
    const { token, headers, ...rest } = options
    const response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      method: 'POST',
      // Content-Type must NOT be set manually for FormData — browser adds the boundary
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body,
    })
    if (response.status === 204) return undefined as T
    const json = await response.json()
    if (!response.ok) {
      const err = json?.error ?? {}
      if (
        response.status === 401 &&
        (err.code === 'TOKEN_EXPIRED' || (token && isJwtExpired(token)))
      ) {
        window.dispatchEvent(new CustomEvent('navega:session-expired'))
      }
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
        json?.errors,
        requestIdFrom(response, err),
      )
    }
    return json.data as T
  }

  return {
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { method: 'GET', ...options }),

    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { method: 'POST', body, ...options }),

    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { method: 'PUT', body, ...options }),

    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { method: 'PATCH', body, ...options }),

    delete: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { method: 'DELETE', ...options }),

    getFull: <T>(path: string, options?: RequestOptions) =>
      requestFull<T>(path, { method: 'GET', ...options }),

    getBlob: (path: string, options?: BlobRequestOptions) =>
      requestBlob(path, { method: 'GET', ...options }),

    postBlob: (path: string, body: unknown, options?: BlobRequestOptions) =>
      requestPostBlob(path, body, options),

    postMultipart: <T>(path: string, body: FormData, options?: BlobRequestOptions) =>
      requestMultipart<T>(path, body, options),
  }
}

export const apiClient = createApiClient('/api/v1')
export const apiV2Client = createApiClient('/api/v2')
